/**
 * Decoding an item's inventory art: fid -> `.frm` -> pixels.
 *
 * Three small formats stacked, all of them documented in the engine and none of
 * them guessed at here.
 *
 * ## fid -> filename (`artBuildFilePath`, engine/src/art.cc:668)
 *
 * A fid packs a rotation, an object type, an animation, a weapon code and a
 * 12-bit art index:
 *
 *     (rotation << 28) | (objectType << 24) | (animType << 16) | (weaponCode << 12) | frmId
 *
 * For everything except critters and heads the path is simply
 * `art\<listName>\<line frmId of that list>`, where `listName` comes from
 * `gArtListDescriptions` (art.cc:79): index 0 is `items`, 7 is `inven`. An
 * item proto's `inventoryFid` has object type 7, so its art is a line in
 * `art/inven/inven.lst`.
 *
 * **The art lists are 0-based**, unlike `proto/items/items.lst`, which is
 * 1-based: `artBuildFilePath` indexes `fileNames + frmId * 13` directly.
 * Getting that backwards is a silent off-by-one that shows the wrong picture
 * for every item, which is why it is stated here rather than inferred.
 * `artReadList` (art.cc:722) cuts each line at the first of ` ,;\r\t\n`, so
 * trailing whitespace and comments are not part of the name.
 *
 * ## `.frm` (`artReadHeader`, art.cc:1142)
 *
 * Big-endian, fixed 62-byte header:
 *
 *     version:u32, framesPerSecond:i16, actionFrame:i16, frameCount:i16,
 *     xOffsets[6]:i16, yOffsets[6]:i16, dataOffsets[6]:i32, dataSize:i32
 *
 * Frames for rotation r begin at `62 + dataOffsets[r]`, each one a 12-byte
 * header — `width:i16, height:i16, size:i32, x:i16, y:i16` — followed by `size`
 * bytes of palette indices. (`artGetFrame` also skips a `paddingForSize`
 * alignment gap; that padding is an in-memory artefact of how the engine lays
 * frames out after loading, not something in the file, and the file's own
 * `dataSize` confirms it: header + 12 + size is exactly the file length for a
 * single-frame inventory art.) Item art is one frame at rotation 0, which is
 * all `decodeFrm` reads.
 *
 * ## The rest of the file (`decodeFrmAnimation`)
 *
 * Critter art is not one frame: `frameCount` is the number of frames **per
 * rotation**, and `dataOffsets[r]` is where that rotation's run of frames
 * begins. The frames of a rotation are consecutive — each one's 12-byte header
 * says how many pixel bytes follow, so the next frame starts right after them.
 * `artGetFrame` (art.cc:1002) walks them the same way.
 *
 * Six rotations are the six facings the engine names ROTATION_NE..ROTATION_N
 * (art_defs.h). Art drawn for a single direction stores one block and points
 * all six offsets at it; equal offsets are how the file says so, and this
 * decoder then keeps one rotation rather than six copies of it.
 *
 * `framesPerSecond` is the header's own playback rate. Stock art that leaves it
 * 0 is not asking for a still image, so 0 falls back to 10 — the rate the
 * shipped critter animations otherwise carry.
 *
 * ## `color.pal` (`colorPaletteLoad`, engine/src/color.cc:284)
 *
 * 256 RGB triples of **6-bit** channels, so each component is scaled by 255/63
 * on the way out. The engine treats a component above 0x3F as an unmapped
 * entry and blacks it out — entry 0 is `FF FF FF` in the stock palette for
 * exactly that reason — and index 0 is the transparent one in every FRM. We
 * take the same two rules: index 0 is transparent, an out-of-range triple is
 * black.
 *
 * Everything is cached, because the catalog renders the same handful of tiles
 * over and over as the player types in the search box.
 */

import type { GameFiles } from './files';

/** `gArtListDescriptions` (art.cc:79), indexed by ObjectType. */
const ART_LIST_NAMES = [
  'items',
  'critters',
  'scenery',
  'walls',
  'tiles',
  'misc',
  'intrface',
  'inven',
  'heads',
  'backgrnd',
  'skilldex',
] as const;

export interface DecodedFrame {
  width: number;
  height: number;
  /** RGBA, straight into `ImageData`. Always backed by a plain ArrayBuffer. */
  rgba: Uint8ClampedArray<ArrayBuffer>;
}

/** A refusal to decode is normal — say which step gave up, for the log. */
export type FrameResult = DecodedFrame | null;

/** Every frame of every rotation an `.frm` holds, ready to be played. */
export interface DecodedAnimation {
  /** The header's `framesPerSecond`, with 0 read as 10 (see the note above). */
  fps: number;
  /** Frames in one rotation — the header's `frameCount`. */
  frameCount: number;
  /** Six facings, or one when all six `dataOffsets` name the same block. */
  rotations: DecodedFrame[][];
}

const objectTypeFromFid = (fid: number): number => (fid >>> 24) & 0x0f;
const frmIdFromFid = (fid: number): number => fid & 0xfff;

/** Cuts an art .lst line where `artReadList` cuts it. */
const artListName = (line: string): string => {
  const cut = line.search(/[ ,;\r\t\n]/);
  return (cut === -1 ? line : line.slice(0, cut)).trim();
};

export class ArtResolver {
  private readonly lists = new Map<number, Promise<string[] | null>>();
  private readonly frames = new Map<number, Promise<FrameResult>>();
  private readonly pathFrames = new Map<string, Promise<FrameResult>>();
  private readonly pathAnimations = new Map<string, Promise<DecodedAnimation | null>>();
  private palette: Promise<Uint8Array | null> | null = null;
  // Written out rather than a constructor parameter property: the test runner
  // imports this module through node's strip-only TypeScript loader, which
  // rejects parameter properties outright (see scripts/ts-resolve.mjs).
  private readonly files: GameFiles;

  constructor(files: GameFiles) {
    this.files = files;
  }

  /** The `art/<list>/<name>` path a fid points at, or null. */
  async pathForFid(fid: number): Promise<string | null> {
    const objectType = objectTypeFromFid(fid);
    const listName = ART_LIST_NAMES[objectType];
    if (!listName) return null;
    // Critters and heads build their filename from animation and weapon codes
    // as well; no item's inventory art is one of those, and a half-right
    // critter path would only produce a wrong picture.
    if (objectType === 1 || objectType === 8) return null;
    const list = await this.list(objectType, listName);
    if (!list) return null;
    const name = list[frmIdFromFid(fid)];
    return name ? `art/${listName}/${name}` : null;
  }

  private list(objectType: number, listName: string): Promise<string[] | null> {
    let pending = this.lists.get(objectType);
    if (!pending) {
      pending = this.files.readText(`art/${listName}/${listName}.lst`).then((text) => {
        if (!text) return null;
        // 0-based, and a trailing newline must not become a phantom entry.
        const lines = text.split(/\r?\n/);
        if (lines.length && lines[lines.length - 1]!.trim() === '') lines.pop();
        return lines.map(artListName);
      });
      this.lists.set(objectType, pending);
    }
    return pending;
  }

  private loadPalette(): Promise<Uint8Array | null> {
    if (!this.palette) {
      this.palette = this.files.read('color.pal').then((bytes) => {
        if (!bytes || bytes.length < 768) return null;
        const out = new Uint8Array(256 * 3);
        for (let i = 0; i < 256; i++) {
          const r = bytes[i * 3]!;
          const g = bytes[i * 3 + 1]!;
          const b = bytes[i * 3 + 2]!;
          // colorPaletteLoad blacks out any triple outside the 6-bit range.
          const mapped = r <= 0x3f && g <= 0x3f && b <= 0x3f;
          out[i * 3] = mapped ? Math.round((r * 255) / 63) : 0;
          out[i * 3 + 1] = mapped ? Math.round((g * 255) / 63) : 0;
          out[i * 3 + 2] = mapped ? Math.round((b * 255) / 63) : 0;
        }
        return out;
      });
    }
    return this.palette;
  }

  /** Frame 0, rotation 0, as RGBA. Null whenever any step declines. */
  frameForFid(fid: number): Promise<FrameResult> {
    let pending = this.frames.get(fid);
    if (!pending) {
      pending = this.decode(fid).catch(() => null);
      this.frames.set(fid, pending);
    }
    return pending;
  }

  /**
   * Frame 0, rotation 0, of an arbitrary `.frm` path, cached by path.
   *
   * `frameForFid` refuses critter fids on purpose (above): their filename also
   * encodes an animation and a weapon code, so a fid alone cannot name the
   * file. A caller that has built the path itself — `art\critters\<base><weapon
   * letter><anim letter>.frm`, the way `_art_get_code` (art.cc:578) builds it —
   * still wants the same palette, decoder and cache, and that is all this is.
   */
  frameForPath(path: string): Promise<FrameResult> {
    let pending = this.pathFrames.get(path);
    if (!pending) {
      pending = this.decodePath(path).catch(() => null);
      this.pathFrames.set(path, pending);
    }
    return pending;
  }

  /**
   * Every frame of every rotation of a `.frm` path, cached by path.
   *
   * Separate from `frameForPath` and separately cached: a critter animation is
   * two orders of magnitude more pixels than the first frame of it, and the
   * grid pickers want only that first frame.
   */
  animationForPath(path: string): Promise<DecodedAnimation | null> {
    let pending = this.pathAnimations.get(path);
    if (!pending) {
      pending = this.decodeAnimationPath(path).catch(() => null);
      this.pathAnimations.set(path, pending);
    }
    return pending;
  }

  private async decodeAnimationPath(path: string): Promise<DecodedAnimation | null> {
    const [bytes, palette] = await Promise.all([this.files.read(path), this.loadPalette()]);
    if (!bytes || !palette) return null;
    return decodeFrmAnimation(bytes, palette);
  }

  private async decode(fid: number): Promise<FrameResult> {
    const path = await this.pathForFid(fid);
    if (!path) return null;
    return this.decodePath(path);
  }

  private async decodePath(path: string): Promise<FrameResult> {
    const [bytes, palette] = await Promise.all([this.files.read(path), this.loadPalette()]);
    if (!bytes || !palette) return null;
    return decodeFrm(bytes, palette);
  }
}

/** Header sizes, from `artReadHeader` and the `ArtFrame` struct. */
const FRM_HEADER_BYTES = 62;
const FRAME_HEADER_BYTES = 12;
/** An inventory tile is tens of pixels square; a megapixel is a misparse. */
const MAX_FRAME_PIXELS = 1 << 22;
/** All six rotations of a critter animation together, as a misparse ceiling. */
const MAX_ANIMATION_PIXELS = 1 << 24;
/** The longest shipped critter animation is tens of frames, not thousands. */
const MAX_ANIMATION_FRAMES = 1 << 10;
/** `framesPerSecond` of 0 plays at the rate the rest of the art asks for. */
const DEFAULT_FPS = 10;

/**
 * One frame at `at`, plus where the next frame starts. Null when the frame
 * header describes something outside the file or outside reason.
 */
function readFrame(
  view: DataView,
  bytes: Uint8Array,
  at: number,
  palette: Uint8Array
): { frame: DecodedFrame; next: number } | null {
  if (at < 0 || at + FRAME_HEADER_BYTES > bytes.length) return null;
  const width = view.getInt16(at, false);
  const height = view.getInt16(at + 2, false);
  const size = view.getInt32(at + 4, false);
  if (width <= 0 || height <= 0) return null;
  if (size !== width * height) return null;
  if (size > MAX_FRAME_PIXELS) return null;

  const pixelsAt = at + FRAME_HEADER_BYTES;
  if (pixelsAt + size > bytes.length) return null;

  const rgba = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < size; i++) {
    const index = bytes[pixelsAt + i]!;
    if (index === 0) continue; // transparent, and already zeroed
    rgba[i * 4] = palette[index * 3]!;
    rgba[i * 4 + 1] = palette[index * 3 + 1]!;
    rgba[i * 4 + 2] = palette[index * 3 + 2]!;
    rgba[i * 4 + 3] = 255;
  }
  return { frame: { width, height, rgba }, next: pixelsAt + size };
}

/** Frame 0 of rotation 0, with `palette` as 256 RGB triples of 0..255. */
export function decodeFrm(bytes: Uint8Array, palette: Uint8Array): FrameResult {
  if (bytes.length < FRM_HEADER_BYTES + FRAME_HEADER_BYTES) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  const frameCount = view.getInt16(8, false);
  if (frameCount < 1) return null;
  const dataOffset = view.getInt32(34, false); // dataOffsets[0]
  if (dataOffset < 0) return null;

  return readFrame(view, bytes, FRM_HEADER_BYTES + dataOffset, palette)?.frame ?? null;
}

/** Every frame of every rotation, same palette rules as `decodeFrm`. */
export function decodeFrmAnimation(bytes: Uint8Array, palette: Uint8Array): DecodedAnimation | null {
  if (bytes.length < FRM_HEADER_BYTES + FRAME_HEADER_BYTES) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  const frameCount = view.getInt16(8, false);
  if (frameCount < 1 || frameCount > MAX_ANIMATION_FRAMES) return null;

  const offsets: number[] = [];
  for (let r = 0; r < 6; r++) offsets.push(view.getInt32(34 + r * 4, false));
  // Equal offsets are the file saying it was drawn for one direction only.
  const singleRotation = offsets.every((o) => o === offsets[0]);

  let budget = MAX_ANIMATION_PIXELS;
  const rotations: DecodedFrame[][] = [];
  for (const offset of singleRotation ? offsets.slice(0, 1) : offsets) {
    if (offset < 0) return null;
    // Frames of a rotation are consecutive; each header says how far the next
    // one is (artGetFrame, art.cc:1002).
    let at = FRM_HEADER_BYTES + offset;
    const frames: DecodedFrame[] = [];
    for (let i = 0; i < frameCount; i++) {
      const read = readFrame(view, bytes, at, palette);
      if (!read) return null;
      budget -= read.frame.width * read.frame.height;
      if (budget < 0) return null;
      frames.push(read.frame);
      at = read.next;
    }
    rotations.push(frames);
  }

  const fps = view.getUint16(4, false);
  return { fps: fps > 0 ? fps : DEFAULT_FPS, frameCount, rotations };
}
